import { dayOfWeekMap } from "../../constants/constants";
import { PUBLIC_API_URL } from "../../environment";
import { AuthoritySchema } from "../../lib/schemas/authoritySchema";
import { BookingSchema } from "../../lib/schemas/bookingSchema";
import { CarSchema } from "../../lib/schemas/CarSchema";
import { CategorySchema } from "../../lib/schemas/categorySchema";
import { BusinessSchema } from "../../lib/schemas/businessSchema";
import { LoginSchema } from "../../lib/schemas/loginSchema";
import { NotificationSchema } from "../../lib/schemas/notificationSchema";
import {
  RegisterForUpdateSchema,
  RegisterSchema,
  RegisterSchemaForEmail,
} from "../../lib/schemas/registerSchema";
import { ServiceSchema } from "../../lib/schemas/serviceSchema";
import { UserPromptSchema } from "../../lib/schemas/UserPromptSchema";
import { WorkingTimeSchema } from "../../lib/schemas/workingTimeSchema ";
import { AuthorityModel } from "../../types/AuthorityModel";
import {
  BookingData,
  BookingDetailsData,
  GeideaSessionResponse,
  NotifyGeideaResultRequest,
  PaymentData,
  ServiceSales,
} from "../../types/booking";
import { CarData } from "../../types/car";
import { CategoryData } from "../../types/category";
import { ContactData } from "../../types/contact";
import { DocumentModel, DocumentUpdateModel } from "../../types/Document";
import { DropdownType } from "../../types/Dropdown";
import { NotificationData } from "../../types/notification";
import { PagedResponse } from "../../types/pagination";
import { ServiceData } from "../../types/service";
import { User, UserIdAndName } from "../../types/User";
import { WorkingTimeData } from "../../types/workingTime";
import { store } from "../stores/store";
import axios, { AxiosError, AxiosResponse } from "axios";
import { ContactSchema } from "../../lib/schemas/contactSchema";
import { LostData } from "../../types/lost";
import { LostSchema } from "../../lib/schemas/lostSchema";
import { ComplaintSchema } from "../../lib/schemas/complaintSchema";
import { ComplaintData } from "../../types/complaints";
import {
  CanceledReason,
  PartnerpaymentType,
  paymentType,
  RefundStatus,
} from "../../lib/utils";
import { NationalityData } from "../../types/nationality";
import { DocumentBookingSchema } from "../../lib/schemas/documentBookingSchema";
import { BlogDetailsData } from "../../types/blog";
import { BlogSchema } from "../../lib/schemas/BlogSchema";
import { ClientData } from "../../types/client";
import { ClientSchema } from "../../lib/schemas/clientScema";
import { EventData } from "../../types/event";
import { EventSchema } from "../../lib/schemas/EventSchema";
import { FileResponseData } from "../../types/filesTypes";
import { EventPROData } from "../../types/eventPRO";
import { EventPROSchema } from "../../lib/schemas/EventPROSchema";
import { KsaBookingFormSchema } from "../../lib/schemas/ksaBookingSchema";
import { RefundRequestFormData } from "../../lib/schemas/refundRequestSchema";
import {
  ResortBookingData,
  ResortBookingDetailsData,
} from "../../types/resortbooking";
import { ResortBookingSchema } from "../../lib/schemas/resortBookingSchema";

axios.defaults.baseURL = PUBLIC_API_URL;

const responseBody = <T>(response: AxiosResponse<T>) => response.data;

axios.interceptors.request.use((config) => {
  const token = store.userStore.token;
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`;
    if (config.data instanceof FormData) {
      config.headers["Content-Type"] = "multipart/form-data";
    } else {
      config.headers["Content-Type"] = "application/json";
    }
  }
  return config;
});

axios.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    const { data, status } = error.response || {};
    if (
      error.code === "ECONNABORTED" ||
      error.message.includes("Network Error")
    ) {
      throw "The backend server is not reachable. Please check if the backend is running.";
    }

    switch (status) {
      case 400:
        if (Array.isArray(data)) {
          const modalStateErrors: string[] = [];
          for (const key in data) {
            if (data[key]) {
              modalStateErrors.push(data[key].description);
            }
          }
          throw modalStateErrors;
        } else {
          throw typeof data === "string" ? data : "Something went wrong";
        }

      case 401:
        throw "Unauthorized";
      case 403:
        throw "Forbidden";
      case 404:
        throw "Not Found";
      case 500:
        throw "Internal Server Error";
      default:
        throw "An unknown error occurred";
    }
  }
);

const requests = {
  get: <T>(url: string) => axios.get<T>(url).then(responseBody),
  post: <T>(url: string, body: object) =>
    axios.post<T>(url, body).then(responseBody),
  put: <T>(url: string, body: object) =>
    axios.put<T>(url, body).then(responseBody),
  del: <T>(url: string) => axios.delete<T>(url).then(responseBody),
};

const Cars = {
  getAll: (params: URLSearchParams) =>
    axios.get<PagedResponse<CarData[]>>("cars", { params }).then(responseBody),
  getById: (id: string) => requests.get<CarData>(`cars/${id}`),
  create: (car: CarSchema) => requests.post<CarData>("cars", car),
  delete: (id: string) => requests.del<string>(`cars/${id}`),
};

const Blogs = {
  getAll: (params: URLSearchParams) =>
    axios
      .get<PagedResponse<BlogDetailsData[]>>("blog", { params })
      .then(responseBody),
  getById: (id: string) => requests.get<BlogDetailsData>(`blog/${id}`),
  create: (blog: BlogSchema) => requests.post<BlogDetailsData>("blog", blog),
  delete: (id: string) => requests.del<string>(`blog/${id}`),
};

const Events = {
  getAll: (params: URLSearchParams) =>
    axios
      .get<PagedResponse<EventData[]>>("event", { params })
      .then(responseBody),
  getById: (id: string) => requests.get<EventData>(`event/${id}`),
  create: (event: EventSchema) => requests.post<EventData>("event", event),
};

const EventPROs = {
  getAll: (params: URLSearchParams) =>
    axios
      .get<PagedResponse<EventPROData[]>>("eventPRO", { params })
      .then(responseBody),
  getById: (id: string) => requests.get<EventPROData>(`eventPRO/${id}`),
  create: (eventPRO: EventPROSchema) =>
    requests.post<EventPROData>("eventPRO", eventPRO),
};

const Contacts = {
  getAll: (params: URLSearchParams) =>
    axios
      .get<PagedResponse<ContactData[]>>("contacts", { params })
      .then(responseBody),
  getById: (id: string) => requests.get<ContactData>(`contacts/${id}`),
  create: (contact: ContactSchema | BusinessSchema) =>
    requests.post<ContactData>("contacts", contact),
  delete: (id: string) => requests.del<string>(`contacts/${id}`),
};
const Losts = {
  getAll: (params: URLSearchParams) =>
    axios.get<PagedResponse<LostData[]>>("lost", { params }).then(responseBody),
  getById: (id: string) => requests.get<LostData>(`lost/${id}`),
  create: (lost: LostSchema) => requests.post<LostData>("lost", lost),
  delete: (id: string) => requests.del<string>(`lost/${id}`),
  uploadImage: (formData: FormData) =>
    axios.post<string>("lost/upload-image", formData).then(responseBody),
  setStatusInProcess: (id: string) =>
    requests.put<string>(`lost/${id}/status/in-process`, {}),
  setStatusCompleted: (id: string) =>
    requests.put<string>(`lost/${id}/status/completed`, {}),
};
const Complaints = {
  getAll: (params: URLSearchParams) =>
    axios
      .get<PagedResponse<ComplaintData[]>>("complaint", { params })
      .then(responseBody),
  getById: (id: string) => requests.get<ComplaintData>(`complaint/${id}`),
  create: (complaint: ComplaintSchema) =>
    requests.post<ComplaintData>("complaint", complaint),
  delete: (id: string) => requests.del<string>(`complaint/${id}`),
  setStatusInProcess: (id: string) =>
    requests.put<string>(`complaint/${id}/status/in-process`, {}),
  setStatusCompleted: (id: string) =>
    requests.put<string>(`complaint/${id}/status/completed`, {}),
};

const Clients = {
  listAll: () =>
    axios
      .get<ClientData[]>("client/all") // this assumes backend supports it
      .then(responseBody),

  getAll: (params: URLSearchParams) =>
    axios
      .get<PagedResponse<ClientData[]>>("client", { params })
      .then(responseBody),
  getById: (id: string) => requests.get<ClientData>(`client/${id}`),
  updateVisaDates: (id: string, data: any) =>
    axios.put(`/client/${id}/visa-dates`, data),

  getByBookingId: (bookingId: string) =>
    axios
      .get<ClientData[]>(`/client/by-booking/${bookingId}`)
      .then((res) => res.data),
  create: (client: {
    name: string;
    passportNumber: string;
    bookingId?: string | undefined;
  }) => requests.post<ClientData>("client", client),
  delete: (id: string) => requests.del<string>(`client/${id}`),
  setStatusInProcess: (id: string) =>
    requests.put<string>(`client/${id}/status/in-process`, {}),
  setStatusRejected: (id: string) =>
    requests.put<string>(`client/${id}/status/rejected`, {}),
  setStatusAccepted: (id: string) =>
    requests.put<string>(`client/${id}/status/accepted`, {}),
  uploadImage: (formData: FormData) =>
    axios
      .post<{
        status: string;
        files: FileResponseData[];
      }>("client/upload-image", formData)
      .then(responseBody),
  sendEmail: (body: { email: string; files: FileResponseData[] }) =>
    axios.post<string>("client/send-email", body).then(responseBody),
};

const Categories = {
  getAll: (params: URLSearchParams) =>
    axios
      .get<PagedResponse<CategoryData[]>>("categories", { params })
      .then(responseBody),
  getById: (id: string) => requests.get<CategoryData>(`categories/${id}`),
  listForDropdown: () =>
    requests.get<CategoryData[]>("categories/authorities-dropdown"),
  create: (formData: CategorySchema) =>
    requests.post<CategoryData>("categories", formData),
  delete: (id: string) => requests.del<string>(`categories/${id}`),
  uploadImage: (formData: FormData) =>
    axios.post<string>("categories/upload-image", formData).then(responseBody),
};

const Services = {
  getAll: (params: URLSearchParams) =>
    axios
      .get<PagedResponse<ServiceData[]>>("services/getAll", { params })
      .then(responseBody),
  getById: (id: string) => requests.get<ServiceData>(`services/${id}`),

  create: (formData: ServiceSchema) =>
    requests.post<ServiceData>("services/create-service", formData),

  uploadImage: (formData: FormData) =>
    axios.post<string>("services/upload-image", formData).then(responseBody),
  update: (id: string, service: ServiceSchema) =>
    requests.put<string>(`services/${id}`, service),
  delete: (id: string) => requests.del<string>(`services/${id}`),
};

const WorkingTime = {
  getAll: () => requests.get<WorkingTimeData[]>("workingtime"),
  create: (workingTime: WorkingTimeSchema) => {
    const mappedData = {
      ...workingTime,
      day: dayOfWeekMap[workingTime.day],
    };
    return requests.post<WorkingTimeData>("workingtime", mappedData);
  },
};

const Payment = {

  createGeideaSession: (data: any): Promise<GeideaSessionResponse> =>
    requests.post(`geidea/create-session`, data),

  notifyGeideaResult: (payload: NotifyGeideaResultRequest) =>
    requests.post<{ ok: boolean }>("/payment/geidea/client-notify", payload),

  uploadBankTransferReceipt: (formData: FormData) =>
    axios.post("payment/upload-bank-receipt", formData).then((res) => res.data),

  initiate: (data: FormData) => requests.post("payment/initiate-payment", data),
  callback: (data: FormData) => requests.post("payment/payment-callback", data),
  getById: (id: string) => requests.get(`payment/${id}`),
  processGooglePayPayment: (paymentData: any) =>
    requests.post(`payment/google-pay`, paymentData),
};
// Helper function to ensure the time is in the correct format (HH:mm)

const ResortBookings = {
  // create:(booking: ResortBookingSchema) => {
  //   console.log("📦 Sending booking:", booking);
  //   return axios.post("resortbooking", booking);
  // },
  setPaymentTypeOfBooking: (id: string, type: paymentType) =>
    requests.put<string>(`resortbooking/${id}/setPaymentType`, type),

  setStatusInProcess: (id: string) =>
    requests.put<string>(`resortbooking/${id}/status/in-process`, {}),
  setStatusCanceled: (id: string, reason: CanceledReason) =>
    requests.put<string>(`resortbooking/${id}/status/canceled`, reason),
  setStatusCompleted: (id: string) =>
    requests.put<string>(`resortbooking/${id}/status/completed`, {}),
  setStatusPending: (id: string) =>
    requests.put<string>(`resortbooking/${id}/status/pending`, {}),
  getUnavailableDates: (serviceId: string, startDate: Date, endDate: Date) =>
    requests.get<UnavailableDatesDto>(
      `resortbooking/unavailable-dates/${serviceId}?startDate=${startDate.toISOString()}&endDate=${endDate.toISOString()}`
    ),

  getById: (id: string) =>
    requests.get<ResortBookingDetailsData>(`resortbooking/${id}`),

  listByServiceId: (serviceId: string) =>
    requests.get<ResortBookingData[]>(`resortbooking/by-service/${serviceId}`),

  create: (booking: ResortBookingSchema) =>
    requests.post<ResortBookingData>("resortbooking", booking),

  addPayment: (payment: {
    bookingId: string;
    payment: {
      transactionAmount: number;
      transactionID: string;
      createDate: string;
      currency: string;
      status: string;
      transactionStatus: string;
      orderName: string;
      merchantId: string;
    };
  }) => requests.post<string>("payment/payments", payment),
};

const Bookings = {
  addPayment: (payment: {
    bookingId: string;
    payment: {
      transactionAmount: number;
      transactionID: string;
      createDate: string;
      currency: string;
      status: string;
      transactionStatus: string;
      orderName: string;
      merchantId: string;
    };
  }) => requests.post<string>("payment/payments", payment),

  addPartnerPayment: (payment: {
    partnerId: string;
    payment: {
      transactionAmount: number;
      transactionID: string;
      createDate: string;
      currency: string;
      status: string;
      transactionStatus: string;
      orderName: string;
      merchantId: string;
    };
  }) => requests.post<string>("payment/payments", payment),
  // Add this method:

  getForAirportCashier: (params?: URLSearchParams) =>
    axios
      .get<ServiceSales[]>("booking/Airportbookings", { params })
      .then(responseBody),

  createRequest: (refundData: RefundRequestFormData) =>
    requests.post("booking/refund-request", refundData),

  updatePaymentStatus: (
    bookingId: string,
    status: "Pending" | "Completed" | "Failed"
  ) => requests.put<string>(`booking/${bookingId}/payment-status`, { status }),

  getAll: (params: URLSearchParams) =>
    axios
      .get<PagedResponse<BookingData[]>>("booking", { params },)
      .then(responseBody),
  
  // getAll: (params: {
  //   pageNumber: number;
  //   pageSize: number;
  //   searchTerm?: string | null;
  //   bookingStatus?: string | null;
  //   paymentStatus?: string | null;
  //   from?: string | null;
  //   to?: string | null;
  //   serviceId?: string | null;
  //   bookingCode?: string | null;
  //   invoiceNo?: string | null;
  // }) =>
  //   axios
  //     .get<PagedResponse<BookingData[]>>("booking", { params })
  //     .then(responseBody),

  getAllWithoutPagination: () =>
    axios
      .get<BookingData[]>("booking/all") // this assumes backend supports it
      .then(responseBody),

  getAllForUber: () => requests.get<BookingData[]>("booking/Get-All-For-Uber"),
  getById: (id: string) => requests.get<BookingDetailsData>(`booking/${id}`),
  getBookingByCustomerId: (id: string) =>
    requests.get<BookingData[]>(`booking/Get-Booking-By-Customer-Id/${id}`),
  getBookingByEmail: (email: string) =>
    requests.get<BookingData[]>(
      `booking/Get-Booking-By-Email?email=${encodeURIComponent(email)}`
    ),
  getAvailableSlots: (date: string) =>
    requests.get<string[]>(`booking/available-slots/${date}`),
  create: (
    booking: BookingSchema | DocumentBookingSchema | KsaBookingFormSchema
  ) => requests.post<BookingData>("booking", booking),
  delete: (id: string) => requests.del<string>(`booking/${id}`),
  uploadImage: (formData: FormData) =>
    
    axios.post<string>("booking/upload-image", formData).then(responseBody),
// agent.ts (Bookings section)
uploadBookingFiles: (formData: FormData) =>
  axios.post<string>("/booking/upload-images", formData).then(responseBody),

  getReport: (params: URLSearchParams) =>
    axios
      .get<PagedResponse<BookingData[]>>("booking/report", { params })
      .then(responseBody),

  // Dropdown
  dropdownList: () => requests.get<DropdownType[]>("booking/get-all-dropdown"),

  setPaymentTypeOfBooking: (id: string, type: paymentType) =>
    requests.put<string>(`booking/${id}/setPaymentType`, type),

  updateRefundStatus: (
    refundRequestId: string,
    data: { status: RefundStatus }
  ) =>
    requests.put<string>(
      `booking/refund-requests/${refundRequestId}/status`,
      data
    ),
  updateAdditionalPrice: (
    bookingId: string,
    data: { additionalPrice: number }
  ) =>
    requests.put<string>(
      `booking/additional-price/${bookingId}/add-price`,
      data
    ),

  // Updated status methods
  setStatusInProcess: (id: string) =>
    requests.put<string>(`booking/${id}/status/in-process`, {}),
  setStatusCanceled: (id: string, reason: CanceledReason) =>
    requests.put<string>(`booking/${id}/status/canceled`, reason),
  setStatusCompleted: (id: string) =>
    requests.put<string>(`booking/${id}/status/completed`, {}),
  setStatusPending: (id: string) =>
    requests.put<string>(`booking/${id}/status/pending`, {}),
};

const Account = {
  addPayment: (payment: PaymentData) =>
    axios.post(`account/addPayment`, payment),

  setPaymentTypeOfPartner: (id: string, type: PartnerpaymentType) =>
    requests.put<string>(`account/${id}/setPaymentType`, type),

  getPendingPartners: (params: URLSearchParams) =>
    axios
      .get<PagedResponse<User[]>>("account/pending-partners", { params })
      .then(responseBody),

  approvePartner: (id: string) =>
    requests.post<string>(`account/${id}/approve-partner`, {}),

  // In your agent.ts
  rejectPartner: (id: string, rejectionReason: string) =>
    requests.post<string>(`account/${id}/reject-partner`, { rejectionReason }),

  current: () => requests.get<User>("account"),
  getUsersIdAndName: (params: URLSearchParams) =>
    axios
      .get<
        PagedResponse<UserIdAndName[]>
      >("account/getUsersIdAndName", { params })
      .then(responseBody),
  login: (user: LoginSchema) => requests.post<User>("account/login", user),
  checkUserExistsByEmail: (email: string) =>
    requests.get<boolean>(
      `account/check-user-exists?email=${encodeURIComponent(email)}`
    ),
  register: (user: RegisterSchema) =>
    requests.post<User>("account/register", user),

  partnerRegister: (formData: FormData) =>
    axios.post("account/partner-register", formData),
  registerWithEmail: (user: RegisterSchemaForEmail) =>
    requests.post<User>("account/register-with-otp", user),
  delete: (id: string) => requests.del(`account/${id}`),
  update: (id: string, userDto: RegisterForUpdateSchema) =>
    requests.put<string>(`account/${id}`, userDto),
  getById: (id: string) =>
    requests.get<UserIdAndName>(`account/GetUserById/${id}`),
};

const AiHelper = {
  getAIResult: (userPrompt: UserPromptSchema) =>
    requests.post<string>("ChatGpt/generate-summary", userPrompt),
};

const Documents = {
  list: (params: URLSearchParams) =>
    axios
      .get<PagedResponse<DocumentModel[]>>("documents", { params })
      .then(responseBody),
  getById: (id: string) => requests.get<DocumentModel>(`documents/${id}`),
  create: (document: FormData) =>
    requests.post<DocumentModel>("documents", document),
  update: (id: string, documentUpdateDto: DocumentUpdateModel) =>
    requests.put<string>(`documents/${id}`, documentUpdateDto),
  delete: (id: string) => requests.del<string>(`documents/${id}`),
};

const Authority = {
  list: (params: URLSearchParams) =>
    axios
      .get<PagedResponse<AuthorityModel[]>>("authority/getAll", { params })
      .then(responseBody),
  listForDropdown: () =>
    requests.get<DropdownType[]>("authority/get-all-dropdown"),
  getById: (id: string) => requests.get<AuthorityModel>(`authority/${id}`),
  create: (document: AuthoritySchema) =>
    requests.post<AuthorityModel>("authority", document),
  update: (id: string, documentUpdateDto: AuthoritySchema) =>
    requests.put<string>(`authority/${id}`, documentUpdateDto),
  delete: (id: string) => requests.del<string>(`authority/${id}`),
};

const Notifications = {
  getAll: (params: URLSearchParams) =>
    axios
      .get<PagedResponse<NotificationData[]>>("notification", { params })
      .then(responseBody),
  ReadToggle: (id: string) => requests.put<string>(`notification/${id}`, {}),
  create: (notification: NotificationSchema) =>
    requests.post<NotificationData>("notification", notification),
  delete: (id: string) => requests.del<string>(`notification/${id}`),
};
const Nationality = {
  getAll: () => requests.get<NationalityData[]>("nationality"),
  getById: (id: string) => requests.get<NationalityData>(`nationality/${id}`),
};

// const Files = {
//   uploadFile: (formData: FormData) =>
//     axios
//       .post<FileResponseData>("files/uploadFile", formData, {})
//       .then(responseBody),

//   updateFile: (id: string, formData: FormData) =>
//     axios
//       .put<FileResponseData>(`files/updateFile/${id}`, formData)
//       .then(responseBody),

//   updateImage: (id: string, formData: FormData) =>
//     axios
//       .put<FileResponseData>(`files/updateImage/${id}`, formData)
//       .then(responseBody),

//   deleteFile: (id: string) =>
//     axios.delete<string>(`files/deleteFile/${id}`).then(responseBody),

//   deleteImage: (id: string) =>
//     axios.delete<string>(`files/deleteImage/${id}`).then(responseBody),
// };

const agent = {
  Account,
  AiHelper,
  Documents,
  Authority,
  Cars,
  Categories,
  Services,
  WorkingTime,
  Bookings,
  ResortBookings,
  Notifications,
  Contacts,
  Payment,
  Losts,
  Complaints,
  Nationality,
  Blogs,
  Clients,
  Events,
  EventPROs,
};

export default agent;
